import {
  CURRENCIES,
  formatCrossborderAmount,
  formatCurrencyAmount,
  formatFxRate,
  formatPercent,
  getCrossborderCurrencySymbol,
  getCurrency,
  getMockFxRate,
  IOF_PERCENT,
  TRANSFER_METHOD_FEES,
} from "../ContaInternacional/constants";
import { formatMonetaryValue } from "../../../Utils/formatters";

export {
  CURRENCIES,
  formatCrossborderAmount,
  formatCurrencyAmount,
  formatFxRate,
  formatPercent,
  getCrossborderCurrencySymbol,
  getCurrency,
};

export const CROSSBORDER_HOME_PATH = "/conta-internacional/crossborder";
export const CROSSBORDER_CUSTOMERS_PATH = "/conta-internacional/crossborder/customers";
export const CROSSBORDER_CUSTOMER_DETAIL_PATH = "/conta-internacional/crossborder/customers/:customerId";
export const CROSSBORDER_PAYIN_PATH = "/conta-internacional/crossborder/payin";
export const CROSSBORDER_PAYOUT_PATH = "/conta-internacional/crossborder/payout";

export const VALUT_JURISDICTION = {
  country: "BR",
  countryLabel: "Brasil (Valut)",
};

export const COUNTRIES = [
  { id: "us", code: "US", label: "Estados Unidos", currencyId: "usd" },
  { id: "br", code: "BR", label: "Brasil", currencyId: "brl" },
  { id: "de", code: "DE", label: "Alemanha", currencyId: "eur" },
  { id: "py", code: "PY", label: "Paraguai", currencyId: "pyg" },
  { id: "mx", code: "MX", label: "México", currencyId: "mxn" },
  { id: "ca", code: "CA", label: "Canadá", currencyId: "cad" },
  { id: "au", code: "AU", label: "Austrália", currencyId: "aud" },
  { id: "jp", code: "JP", label: "Japão", currencyId: "jpy" },
  { id: "ch", code: "CH", label: "Suíça", currencyId: "chf" },
  { id: "se", code: "SE", label: "Suécia", currencyId: "sek" },
];

export const PAYMENT_RAILS = {
  wire: { id: "wire", label: "WIRE", description: "Transferência bancária internacional (Wire)" },
  ach: { id: "ach", label: "ACH", description: "Transferência eletrônica nos EUA" },
  sepa: { id: "sepa", label: "SEPA", description: "Transferência na zona euro" },
  swift: { id: "swift", label: "SWIFT", description: "Transferência internacional SWIFT" },
  pix: { id: "pix", label: "PIX", description: "Transferência instantânea em BRL" },
  local: { id: "local", label: "BANCO LOCAL", description: "Transferência via rede bancária local" },
};

export const ORIGIN_RAILS_BY_COUNTRY = {
  us: ["wire", "ach", "swift"],
  br: ["pix", "wire", "swift"],
  de: ["sepa", "swift"],
  py: ["swift", "local"],
  mx: ["swift", "local"],
  ca: ["wire", "swift"],
  au: ["swift", "local"],
  jp: ["swift", "local"],
  ch: ["swift", "sepa"],
  se: ["sepa", "swift"],
};

export const VALUT_RECEIVE_RAILS = {
  usd: ["wire", "ach", "swift"],
  eur: ["sepa", "swift"],
  pyg: ["swift"],
  brl: ["pix", "wire"],
  cad: ["swift"],
  mxn: ["swift"],
  aud: ["swift"],
  jpy: ["swift"],
  chf: ["swift"],
  sek: ["swift"],
};

export const VALUT_SEND_RAILS = {
  usd: ["wire", "ach", "swift"],
  eur: ["sepa", "swift"],
  pyg: ["swift"],
  brl: ["pix", "wire", "swift"],
  cad: ["swift"],
  mxn: ["swift"],
  aud: ["swift"],
  jpy: ["swift"],
  chf: ["swift"],
  sek: ["swift"],
};

export const BENEFICIARY_RAILS_BY_COUNTRY = {
  us: ["wire", "ach", "swift"],
  br: ["pix", "wire", "swift"],
  de: ["sepa", "swift"],
  py: ["swift", "local"],
  mx: ["swift", "local"],
  ca: ["wire", "swift"],
  au: ["swift", "local"],
  jp: ["swift", "local"],
  ch: ["swift", "sepa"],
  se: ["sepa", "swift"],
};

export const CROSSBORDER_FEE_PERCENT = 0.45;

export const getCountry = (id) => COUNTRIES.find((c) => c.id === id);

export const getOriginRails = (countryId) =>
  (ORIGIN_RAILS_BY_COUNTRY[countryId] ?? [])
    .map((id) => PAYMENT_RAILS[id])
    .filter(Boolean);

export const getValutReceiveRails = (currencyId) =>
  (VALUT_RECEIVE_RAILS[currencyId] ?? [])
    .map((id) => PAYMENT_RAILS[id])
    .filter(Boolean);

export const getValutSendRails = (currencyId) =>
  (VALUT_SEND_RAILS[currencyId] ?? [])
    .map((id) => PAYMENT_RAILS[id])
    .filter(Boolean);

export const getBeneficiaryRails = (countryId) =>
  (BENEFICIARY_RAILS_BY_COUNTRY[countryId] ?? [])
    .map((id) => PAYMENT_RAILS[id])
    .filter(Boolean);

export const getRailFee = (railId, amount) => {
  const feeConfig = TRANSFER_METHOD_FEES[railId];
  if (!feeConfig || !amount) return { fee: 0, feeLabel: "Taxa", feePercent: null };

  const fee =
    feeConfig.type === "percent"
      ? amount * (feeConfig.amount / 100)
      : feeConfig.amount;

  return {
    fee,
    feeLabel: feeConfig.label,
    feePercent: feeConfig.type === "percent" ? feeConfig.amount : null,
  };
};

export const getPayinQuote = ({
  originCurrencyId,
  destinationCurrencyId,
  originRailId,
  destinationRailId,
  amount,
}) => {
  const numericAmount = Number(amount) || 0;
  const hasAmount = numericAmount > 0;
  const originCurrency = getCurrency(originCurrencyId);
  const destinationCurrency = getCurrency(destinationCurrencyId);
  const rate = getMockFxRate(originCurrencyId, destinationCurrencyId);
  const iofApplies = originCurrencyId === "brl" || destinationCurrencyId === "brl";

  if (!hasAmount || !originCurrency || !destinationCurrency) {
    return {
      hasAmount: false,
      originCurrency,
      destinationCurrency,
      rate,
      amount: 0,
      originFee: 0,
      originFeeLabel: "Taxa origem",
      destinationFee: 0,
      destinationFeeLabel: "Taxa destino",
      conversionFee: 0,
      conversionFeePercent: CROSSBORDER_FEE_PERCENT,
      iof: 0,
      iofPercent: IOF_PERCENT,
      iofApplies,
      grossReceive: 0,
      netReceive: 0,
    };
  }

  const originFeeInfo = getRailFee(originRailId, numericAmount);
  const afterOriginFee = Math.max(0, numericAmount - originFeeInfo.fee);

  let effectiveAmount = afterOriginFee;
  let iof = 0;

  if (originCurrencyId === "brl") {
    iof = afterOriginFee * (IOF_PERCENT / 100);
    effectiveAmount = afterOriginFee - iof;
  }

  const grossReceive = effectiveAmount * rate;
  const conversionFee = grossReceive * (CROSSBORDER_FEE_PERCENT / 100);
  let netBeforeDestFee = grossReceive - conversionFee;

  if (destinationCurrencyId === "brl") {
    const destIof = netBeforeDestFee * (IOF_PERCENT / 100);
    iof += destIof;
    netBeforeDestFee -= destIof;
  }

  const destFeeInfo = getRailFee(destinationRailId, netBeforeDestFee);
  const netReceive = Math.max(0, netBeforeDestFee - destFeeInfo.fee);

  return {
    hasAmount: true,
    originCurrency,
    destinationCurrency,
    rate,
    amount: numericAmount,
    originFee: originFeeInfo.fee,
    originFeeLabel: originFeeInfo.feeLabel,
    originFeePercent: originFeeInfo.feePercent,
    destinationFee: destFeeInfo.fee,
    destinationFeeLabel: destFeeInfo.feeLabel,
    destinationFeePercent: destFeeInfo.feePercent,
    conversionFee,
    conversionFeePercent: CROSSBORDER_FEE_PERCENT,
    iof,
    iofPercent: IOF_PERCENT,
    iofApplies,
    grossReceive,
    netReceive,
  };
};

export const getPayoutQuote = ({
  originCurrencyId,
  destinationCurrencyId,
  sendRailId,
  beneficiaryRailId,
  amount,
}) => {
  const numericAmount = Number(amount) || 0;
  const hasAmount = numericAmount > 0;
  const originCurrency = getCurrency(originCurrencyId);
  const destinationCurrency = getCurrency(destinationCurrencyId);
  const rate = getMockFxRate(originCurrencyId, destinationCurrencyId);
  const iofApplies = originCurrencyId === "brl" || destinationCurrencyId === "brl";

  if (!hasAmount || !originCurrency || !destinationCurrency) {
    return {
      hasAmount: false,
      originCurrency,
      destinationCurrency,
      rate,
      amount: 0,
      sendFee: 0,
      sendFeeLabel: "Taxa de envio",
      conversionFee: 0,
      conversionFeePercent: CROSSBORDER_FEE_PERCENT,
      beneficiaryFee: 0,
      beneficiaryFeeLabel: "Taxa destino",
      iof: 0,
      iofPercent: IOF_PERCENT,
      iofApplies,
      grossReceive: 0,
      netReceive: 0,
      totalDebit: 0,
    };
  }

  const sendFeeInfo = getRailFee(sendRailId, numericAmount);
  const totalDebit = numericAmount + sendFeeInfo.fee;

  let effectiveAmount = numericAmount;
  let iof = 0;

  if (originCurrencyId === "brl") {
    iof = numericAmount * (IOF_PERCENT / 100);
    effectiveAmount = numericAmount - iof;
  }

  const grossReceive = effectiveAmount * rate;
  const conversionFee = grossReceive * (CROSSBORDER_FEE_PERCENT / 100);
  let netBeforeBeneficiaryFee = grossReceive - conversionFee;

  if (destinationCurrencyId === "brl") {
    const destIof = netBeforeBeneficiaryFee * (IOF_PERCENT / 100);
    iof += destIof;
    netBeforeBeneficiaryFee -= destIof;
  }

  const beneficiaryFeeInfo = getRailFee(beneficiaryRailId, netBeforeBeneficiaryFee);
  const netReceive = Math.max(0, netBeforeBeneficiaryFee - beneficiaryFeeInfo.fee);

  return {
    hasAmount: true,
    originCurrency,
    destinationCurrency,
    rate,
    amount: numericAmount,
    sendFee: sendFeeInfo.fee,
    sendFeeLabel: sendFeeInfo.feeLabel,
    sendFeePercent: sendFeeInfo.feePercent,
    conversionFee,
    conversionFeePercent: CROSSBORDER_FEE_PERCENT,
    beneficiaryFee: beneficiaryFeeInfo.fee,
    beneficiaryFeeLabel: beneficiaryFeeInfo.feeLabel,
    beneficiaryFeePercent: beneficiaryFeeInfo.feePercent,
    iof,
    iofPercent: IOF_PERCENT,
    iofApplies,
    grossReceive,
    netReceive,
    totalDebit,
  };
};

export const CROSSBORDER_STATUS = {
  completed: {
    id: "completed",
    label: "Concluído",
    badgeClass: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
  refunded: {
    id: "refunded",
    label: "Reembolsado",
    badgeClass: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  },
  failed: {
    id: "failed",
    label: "Falhou",
    badgeClass: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
  },
  processing: {
    id: "processing",
    label: "Processando",
    badgeClass: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  },
};

export const KYC_STATUS = {
  approved: {
    id: "approved",
    labelKey: "crossborder.kyc.statusApproved",
    badgeClass: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
  pending: {
    id: "pending",
    labelKey: "crossborder.kyc.statusPending",
    badgeClass: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  },
  rejected: {
    id: "rejected",
    labelKey: "crossborder.kyc.statusRejected",
    badgeClass: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
  },
  in_review: {
    id: "in_review",
    labelKey: "crossborder.kyc.statusInReview",
    badgeClass: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  },
};

export const MOCK_CUSTOMERS = [
  {
    id: "gzm",
    label: "GZM",
    countryCode: "us",
    type: "Corporativo",
    walletsCount: 3,
    email: "compliance@gzm.io",
    kycStatus: "approved",
    kycApprovedAt: "12/03/2025",
    createdAt: "05/01/2025",
  },
  {
    id: "valut-global",
    label: "VALUT GLOBAL LLC",
    countryCode: "us",
    type: "Corporativo",
    walletsCount: 5,
    email: "ops@valutglobal.com",
    kycStatus: "approved",
    kycStartedAt: "10/12/2024",
    kycApprovedAt: "20/02/2025",
    createdAt: "10/12/2024",
  },
  {
    id: "acme",
    label: "Acme Corporation",
    countryCode: "us",
    type: "Corporativo",
    walletsCount: 2,
    email: "finance@acme.com",
    kycStatus: "approved",
    kycApprovedAt: "08/04/2025",
    createdAt: "15/02/2025",
  },
  {
    id: "globex",
    label: "Globex Industries",
    countryCode: "us",
    type: "Corporativo",
    walletsCount: 1,
    email: "treasury@globex.com",
    kycStatus: "pending",
    kycApprovedAt: null,
    createdAt: "22/05/2025",
  },
  {
    id: "initech",
    label: "Initech Ltda.",
    countryCode: "br",
    type: "Corporativo",
    walletsCount: 2,
    email: "contato@initech.com.br",
    kycStatus: "in_review",
    kycApprovedAt: null,
    createdAt: "01/06/2025",
  },
  {
    id: "umbrella",
    label: "Umbrella Holdings",
    countryCode: "py",
    type: "Corporativo",
    walletsCount: 4,
    email: "payments@umbrella.py",
    kycStatus: "rejected",
    kycApprovedAt: null,
    createdAt: "18/04/2025",
  },
];

export const MOCK_CUSTOMER_PROFILES = {
  "valut-global": {
    legalName: "VALUT GLOBAL LLC",
    dba: "Valut Global",
    formationDate: "15/08/2019",
    email: "ops@valutglobal.com",
    taxId: "84-3921847",
    phone: "+1 (305) 555-0142",
    website: "https://valutglobal.com",
    limits: {
      perTransaction: 50000,
      daily: 150000,
      monthly: 500000,
      currencyId: "usd",
    },
    address: {
      street: "1000 Brickell Avenue, Suite 1200",
      city: "Miami",
      state: "FL",
      zipCode: "33131",
      country: "Estados Unidos",
    },
    virtualAccounts: [
      {
        id: "va-usd",
        token: "usdc",
        network: "Ethereum",
        wire: { accountNumber: "****7821", routingNumber: "021000021", bankName: "JPMorgan Chase" },
        ach: { accountNumber: "****7821", routingNumber: "021000021", bankName: "JPMorgan Chase" },
        swift: { iban: "US33CHAS00000000007821", bic: "CHASUS33" },
      },
    ],
  },
  gzm: {
    legalName: "GZM Technologies Inc.",
    dba: "GZM",
    formationDate: "03/11/2020",
    email: "compliance@gzm.io",
    taxId: "92-1847362",
    phone: "+1 (415) 555-0198",
    website: "https://gzm.io",
    limits: { perTransaction: 25000, daily: 75000, monthly: 250000, currencyId: "usd" },
    address: {
      street: "548 Market Street, Suite 320",
      city: "San Francisco",
      state: "CA",
      zipCode: "94104",
      country: "Estados Unidos",
    },
    virtualAccounts: [],
  },
  acme: {
    legalName: "Acme Corporation",
    dba: "Acme Corp",
    formationDate: "22/06/2015",
    email: "finance@acme.com",
    taxId: "45-2891034",
    phone: "+1 (212) 555-0177",
    website: "https://acme.com",
    limits: { perTransaction: 30000, daily: 100000, monthly: 300000, currencyId: "usd" },
    address: {
      street: "350 Fifth Avenue, Floor 21",
      city: "New York",
      state: "NY",
      zipCode: "10118",
      country: "Estados Unidos",
    },
    virtualAccounts: [],
  },
  globex: {
    legalName: "Globex Industries LLC",
    dba: "Globex",
    formationDate: "10/01/2022",
    email: "treasury@globex.com",
    taxId: "88-1029384",
    phone: "+1 (312) 555-0133",
    website: "https://globex.com",
    limits: { perTransaction: 10000, daily: 25000, monthly: 75000, currencyId: "usd" },
    address: {
      street: "233 S Wacker Drive",
      city: "Chicago",
      state: "IL",
      zipCode: "60606",
      country: "Estados Unidos",
    },
    virtualAccounts: [],
  },
  initech: {
    legalName: "Initech Ltda.",
    dba: "Initech",
    formationDate: "14/09/2018",
    email: "contato@initech.com.br",
    taxId: "12.345.678/0001-90",
    phone: "+55 (11) 3456-7890",
    website: "https://initech.com.br",
    limits: { perTransaction: 50000, daily: 200000, monthly: 600000, currencyId: "brl" },
    address: {
      street: "Av. Paulista, 1000, 12º andar",
      city: "São Paulo",
      state: "SP",
      zipCode: "01310-100",
      country: "Brasil",
    },
    virtualAccounts: [],
  },
  umbrella: {
    legalName: "Umbrella Holdings S.A.",
    dba: "Umbrella",
    formationDate: "05/04/2017",
    email: "payments@umbrella.py",
    taxId: "80012345-6",
    phone: "+595 (21) 555-0100",
    website: "https://umbrella.py",
    limits: { perTransaction: 20000, daily: 60000, monthly: 180000, currencyId: "usd" },
    address: {
      street: "Av. Mariscal López 1234",
      city: "Asunción",
      state: "Central",
      zipCode: "001001",
      country: "Paraguai",
    },
    virtualAccounts: [],
  },
};

export const MOCK_VALUT_ACCOUNTS = [
  { id: "valut-usd", label: "Conta USD Valut", currencyId: "usd" },
  { id: "valut-eur", label: "Conta EUR Valut", currencyId: "eur" },
  { id: "valut-brl", label: "Conta BRL Valut", currencyId: "brl" },
  { id: "valut-pyg", label: "Conta PYG Valut", currencyId: "pyg" },
];

export const PAYIN_DESTINATION_CURRENCIES = ["usd", "eur", "brl", "pyg"];

export const PAYOUT_ORIGIN_CURRENCIES = ["usd", "eur", "brl", "pyg"];

export const PAYOUT_DESTINATION_CURRENCIES = ["usd", "eur", "brl", "pyg"];

export const CROSSBORDER_TOKENS = [
  { id: "usdc", label: "USDC" },
  { id: "usdt", label: "USDT" },
];

export const PAYIN_PAYMENT_METHODS = [
  {
    id: "ach",
    label: "ACH",
    description: "Transferência eletrônica nos EUA",
    countryCode: "us",
    railId: "ach",
  },
  {
    id: "wire",
    label: "WIRE",
    description: "Transferência bancária internacional",
    countryCode: "us",
    railId: "wire",
  },
  {
    id: "sepa",
    label: "SEPA",
    description: "Transferência na zona euro",
    countryCode: "de",
    railId: "sepa",
  },
  {
    id: "swift",
    label: "SWIFT",
    description: "Transferência internacional SWIFT",
    countryCode: "py",
    railId: "swift",
  },
];

export const PAYOUT_NETWORKS = [
  { id: "ethereum", label: "Ethereum" },
  { id: "polygon", label: "Polygon" },
  { id: "solana", label: "Solana" },
  { id: "tron", label: "Tron" },
];

export const MOCK_BANK_ACCOUNTS = [
  {
    id: "chase-4821",
    label: "Chase Bank — ****4821",
    customerId: "acme",
    currencyId: "usd",
    countryId: "us",
    defaultRailId: "wire",
  },
  {
    id: "boa-7733",
    label: "Bank of America — ****7733",
    customerId: "globex",
    currencyId: "usd",
    countryId: "us",
    defaultRailId: "ach",
  },
  {
    id: "itau-9012",
    label: "Itaú — ****9012",
    customerId: "initech",
    currencyId: "brl",
    countryId: "br",
    defaultRailId: "pix",
  },
  {
    id: "itau-py-3344",
    label: "Itaú Paraguay — ****3344",
    customerId: "umbrella",
    currencyId: "pyg",
    countryId: "py",
    defaultRailId: "swift",
  },
  {
    id: "deutsche-5567",
    label: "Deutsche Bank — ****5567",
    customerId: "valut-global",
    currencyId: "eur",
    countryId: "de",
    defaultRailId: "sepa",
  },
];

export const PAYIN_AMOUNT_CURRENCIES = ["usd", "brl", "eur"];

export const PAYIN_AMOUNT_LIMITS = {
  usd: { min: 10, max: 30000 },
  brl: { min: 50, max: 150000 },
  eur: { min: 10, max: 28000 },
};

export const PAYOUT_AMOUNT_LIMITS = {
  usd: { min: 10, max: 30000 },
  eur: { min: 10, max: 28000 },
  brl: { min: 50, max: 150000 },
  pyg: { min: 50000, max: 50000000 },
};

export const getRailLabel = (railId) => {
  const label = PAYMENT_RAILS[railId]?.label ?? railId ?? "";
  return label.toUpperCase();
};

export const formatTransactionRoute = (
  {
    originRailId,
    destinationRailId,
    fromCurrency,
    toCurrency,
    originCurrencyId,
    destinationCurrencyId,
  },
  { showMethods = true } = {}
) => {
  const originCurrency = (originCurrencyId ?? fromCurrency ?? "").toUpperCase();
  const destCurrency = (destinationCurrencyId ?? toCurrency ?? "").toUpperCase();

  if (!originCurrency || !destCurrency) return "—";

  if (!showMethods) {
    return `${originCurrency} → ${destCurrency}`;
  }

  const originPart = `${getRailLabel(originRailId)} ${originCurrency}`.trim();
  const destPart = `${getRailLabel(destinationRailId)} ${destCurrency}`.trim();
  return `${originPart} → ${destPart}`;
};

export const getTransactionOperationFee = (item) => {
  if (item?.operationFee != null) {
    return {
      amount: item.operationFee,
      currencyId: item.operationFeeCurrency ?? item.fromCurrency,
    };
  }

  if (!item?.fromAmount) {
    return { amount: 0, currencyId: item?.fromCurrency ?? "usd" };
  }

  if (item.type === "payin") {
    const quote = getPayinQuote({
      originCurrencyId: item.fromCurrency,
      destinationCurrencyId: item.toCurrency,
      originRailId: item.originRailId,
      destinationRailId: item.destinationRailId,
      amount: item.fromAmount,
    });

    if (!quote.hasAmount) {
      return { amount: 0, currencyId: item.fromCurrency };
    }

    return {
      amount: quote.originFee + quote.conversionFee + quote.destinationFee + quote.iof,
      currencyId: item.fromCurrency,
    };
  }

  const quote = getPayoutQuote({
    originCurrencyId: item.fromCurrency,
    destinationCurrencyId: item.toCurrency,
    sendRailId: item.originRailId,
    beneficiaryRailId: item.destinationRailId,
    amount: item.fromAmount,
  });

  if (!quote.hasAmount) {
    return { amount: 0, currencyId: item.fromCurrency };
  }

  return {
    amount: quote.sendFee + quote.conversionFee + quote.beneficiaryFee + quote.iof,
    currencyId: item.fromCurrency,
  };
};

export const MOCK_HISTORY = [
  {
    id: "cb-007",
    referenceId: "PO_595DA874E627D45",
    type: "payout",
    time: "18/06/2025 11:20",
    senderCustomer: "VALUT GLOBAL LLC",
    originRailId: "wire",
    destinationRailId: "pix",
    fromAmount: 10,
    fromCurrency: "usdc",
    toAmount: 54.8,
    toCurrency: "brl",
    valutAccount: "Conta USD Valut",
    beneficiaryAccount: "Chave PIX beneficiário",
    senderAmount: 10,
    senderAmountCurrency: "usdc",
    receiverAmount: 54.8,
    receiverAmountCurrency: "brl",
    operationFee: 0.15,
    operationFeeCurrency: "usdc",
    status: "completed",
    blockchain: "Polygon",
    network: "Polygon",
    senderAddress: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb",
    countryCode: "br",
    country: "Brasil",
    pixKey: "ops@valutglobal.com",
    tracking: [
      {
        id: "onchain",
        label: "Confirmar USDC na Polygon",
        status: "completed",
        txHash: "0x3f8a2b1c9d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f1",
        completedAt: "18/06/2025 11:21",
      },
      {
        id: "pix",
        label: "Enviar PIX BRL ao beneficiário",
        status: "completed",
        provider: "Valut",
        transferId: "tr_pix_8k2m4n6",
        eta: "Instantâneo",
        completedAt: "18/06/2025 11:23",
        note: "PIX creditado na conta de destino.",
      },
      {
        id: "complete",
        label: "Concluir payout",
        status: "completed",
        completedAt: "18/06/2025 11:23",
      },
    ],
  },
  {
    referenceId: "pi_8f2a91c4",
    type: "payin",
    time: "17/06/2025 14:32",
    senderCustomer: "Acme Corporation",
    originRailId: "pix",
    destinationRailId: "wire",
    fromAmount: 50,
    fromCurrency: "brl",
    toAmount: 9.12,
    toCurrency: "usd",
    valutAccount: "Conta USD Valut",
    originAccount: "Itaú — conta origem",
    senderAmount: 50,
    senderAmountCurrency: "brl",
    receiverAmount: 9.12,
    receiverAmountCurrency: "usd",
    operationFee: 0.44,
    operationFeeCurrency: "brl",
    status: "completed",
    tracking: [
      {
        id: "funds",
        label: "Receber PIX BRL na origem",
        status: "completed",
        completedAt: "17/06/2025 14:32",
      },
      {
        id: "conversion",
        label: "Converter BRL em USD",
        status: "completed",
        completedAt: "17/06/2025 14:35",
      },
      {
        id: "credit",
        label: "Creditar Wire USD na Valut",
        status: "completed",
        completedAt: "17/06/2025 14:36",
      },
    ],
  },
  {
    id: "cb-002",
    referenceId: "pi_3c7d12ef",
    type: "payin",
    time: "16/06/2025 09:15",
    senderCustomer: "Globex Industries",
    originRailId: "ach",
    destinationRailId: "wire",
    fromAmount: 1000,
    fromCurrency: "usd",
    toAmount: 997.5,
    toCurrency: "usd",
    valutAccount: "Conta USD Valut",
    originAccount: "Bank of America — origem",
    senderAmount: 1000,
    senderAmountCurrency: "usd",
    receiverAmount: 997.5,
    receiverAmountCurrency: "usd",
    operationFee: 34.49,
    operationFeeCurrency: "usd",
    status: "processing",
    tracking: [
      {
        id: "funds",
        label: "Receber ACH USD na origem",
        status: "completed",
        completedAt: "16/06/2025 09:15",
      },
      {
        id: "conversion",
        label: "Processar conversão USD",
        status: "current",
      },
      {
        id: "credit",
        label: "Creditar Wire USD na Valut",
        status: "pending",
      },
    ],
  },
  {
    id: "cb-003",
    referenceId: "po_9k4m2x7p",
    type: "payout",
    time: "15/06/2025 18:44",
    senderCustomer: "Initech Ltda.",
    originRailId: "wire",
    destinationRailId: "wire",
    fromAmount: 250,
    fromCurrency: "usd",
    toAmount: 248.2,
    toCurrency: "usd",
    valutAccount: "Conta USD Valut",
    beneficiaryAccount: "Chase Bank — ****4821",
    senderAmount: 250,
    senderAmountCurrency: "usd",
    receiverAmount: 248.2,
    receiverAmountCurrency: "usd",
    operationFee: 26.37,
    operationFeeCurrency: "usd",
    status: "completed",
    blockchain: "Ethereum",
    network: "Ethereum",
    senderAddress: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb",
    countryCode: "us",
    country: "Estados Unidos",
    pixKey: null,
    tracking: [
      {
        id: "debit",
        label: "Debitar Wire USD da Valut",
        status: "completed",
        completedAt: "15/06/2025 18:45",
      },
      {
        id: "wire",
        label: "Iniciar Wire USD ao beneficiário",
        status: "completed",
        provider: "JPMorgan Chase",
        transferId: "tr_w8k3m9x2",
        txHash: "0x8f3a2b1c9d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0",
        eta: "1 dia útil",
        completedAt: "15/06/2025 19:10",
        note: "Transferência confirmada na rede bancária.",
      },
      {
        id: "complete",
        label: "Concluir payout",
        status: "completed",
        completedAt: "16/06/2025 08:22",
        note: "Payout concluído com sucesso.",
      },
    ],
  },
  {
    id: "cb-004",
    referenceId: "pi_5h8n1q6w",
    type: "payin",
    time: "14/06/2025 11:02",
    senderCustomer: "Umbrella Holdings",
    originRailId: "sepa",
    destinationRailId: "wire",
    fromAmount: 500,
    fromCurrency: "eur",
    toAmount: 492.1,
    toCurrency: "usd",
    valutAccount: "Conta USD Valut",
    originAccount: "Deutsche Bank — origem",
    senderAmount: 500,
    senderAmountCurrency: "eur",
    receiverAmount: 492.1,
    receiverAmountCurrency: "usd",
    operationFee: 27.72,
    operationFeeCurrency: "eur",
    status: "failed",
    tracking: [
      {
        id: "funds",
        label: "Receber SEPA EUR na origem",
        status: "completed",
        completedAt: "14/06/2025 11:02",
      },
      {
        id: "conversion",
        label: "Converter EUR em USD",
        status: "current",
      },
      {
        id: "credit",
        label: "Creditar Wire USD na Valut",
        status: "pending",
      },
    ],
  },
  {
    id: "cb-005",
    referenceId: "po_2j7r4t8v",
    type: "payout",
    time: "13/06/2025 16:28",
    senderCustomer: "Acme Corporation",
    originRailId: "wire",
    destinationRailId: "ach",
    fromAmount: 1500,
    fromCurrency: "usd",
    toAmount: 1492.5,
    toCurrency: "usd",
    valutAccount: "Conta USD Valut",
    beneficiaryAccount: "Bank of America — ****7733",
    senderAmount: 1500,
    senderAmountCurrency: "usd",
    receiverAmount: 1492.5,
    receiverAmountCurrency: "usd",
    operationFee: 36.74,
    operationFeeCurrency: "usd",
    status: "processing",
    blockchain: "Polygon",
    network: "Polygon",
    senderAddress: "0x9a3f2e1d8c7b6a5948372615049382716059483726",
    countryCode: "us",
    country: "Estados Unidos",
    pixKey: null,
    tracking: [
      {
        id: "debit",
        label: "Debitar Wire USD da Valut",
        status: "completed",
        completedAt: "13/06/2025 16:29",
      },
      {
        id: "ach",
        label: "Iniciar ACH USD ao beneficiário",
        status: "current",
        provider: "Bank of America",
        transferId: "tr_a4n8p2k6",
        txHash: "0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2",
        eta: "1-2 dias úteis",
      },
      {
        id: "complete",
        label: "Concluir payout",
        status: "pending",
      },
    ],
  },
  {
    id: "cb-006",
    referenceId: "po_6m3s9w1z",
    type: "payout",
    time: "12/06/2025 10:05",
    senderCustomer: "VALUT GLOBAL LLC",
    originRailId: "wire",
    destinationRailId: "sepa",
    fromAmount: 3200,
    fromCurrency: "usd",
    toAmount: 2948.5,
    toCurrency: "eur",
    valutAccount: "Conta USD Valut",
    beneficiaryAccount: "Deutsche Bank — ****5567",
    senderAmount: 3200,
    senderAmountCurrency: "usd",
    receiverAmount: 2948.5,
    receiverAmountCurrency: "eur",
    operationFee: 39.65,
    operationFeeCurrency: "usd",
    status: "refunded",
    blockchain: "Ethereum",
    network: "Ethereum",
    senderAddress: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb",
    countryCode: "de",
    country: "Alemanha",
    pixKey: null,
    tracking: [
      {
        id: "debit",
        label: "Debitar Wire USD da Valut",
        status: "completed",
        completedAt: "12/06/2025 10:06",
      },
      {
        id: "sepa",
        label: "Iniciar SEPA EUR ao beneficiário",
        status: "completed",
        provider: "Wise",
        transferId: "tr_s5k9m2x7",
        txHash: "0x4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5",
        eta: "1-2 dias úteis",
        completedAt: "12/06/2025 10:18",
        note: "Reembolso processado após falha no destino.",
      },
      {
        id: "complete",
        label: "Concluir payout",
        status: "completed",
        completedAt: "13/06/2025 09:40 — reembolsado",
      },
    ],
  },
];

export const getCustomer = (id) => MOCK_CUSTOMERS.find((c) => c.id === id);
export const getCustomerProfile = (id) => MOCK_CUSTOMER_PROFILES[id] ?? null;
export const getValutAccount = (id) => MOCK_VALUT_ACCOUNTS.find((a) => a.id === id);
export const getToken = (id) => CROSSBORDER_TOKENS.find((t) => t.id === id);
export const getPaymentMethodCountry = (method) =>
  method?.countryCode ? getCountry(method.countryCode) : undefined;

export const getPaymentMethodCurrency = (method) => {
  const country = getPaymentMethodCountry(method);
  return country?.currencyId ? getCurrency(country.currencyId) : undefined;
};

export const formatOriginPaymentMethodLabel = (method) => {
  if (!method) return "";

  const country = getPaymentMethodCountry(method);
  const currency = getPaymentMethodCurrency(method);
  const railLabel = PAYMENT_RAILS[method.railId]?.label ?? method.label ?? "";

  if (!country) return railLabel;
  if (!currency) return `${country.label} · ${railLabel}`;
  return `${country.label} · ${currency.code} · ${railLabel}`;
};

export const getPaymentMethodSearchText = (method) => {
  if (!method) return "";

  const country = getPaymentMethodCountry(method);
  const currency = getPaymentMethodCurrency(method);
  const railLabel = PAYMENT_RAILS[method.railId]?.label ?? method.label ?? "";

  return [
    method.id,
    method.label,
    method.description,
    country?.label,
    country?.code,
    currency?.code,
    currency?.label,
    railLabel,
    formatOriginPaymentMethodLabel(method),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export const getPaymentMethod = (id) => PAYIN_PAYMENT_METHODS.find((m) => m.id === id);
export const getNetwork = (id) => PAYOUT_NETWORKS.find((n) => n.id === id);
export const getBankAccount = (id) => MOCK_BANK_ACCOUNTS.find((a) => a.id === id);
export const getRail = (id) => PAYMENT_RAILS[id];

export const getValutAccountsForCurrency = (currencyId) =>
  MOCK_VALUT_ACCOUNTS.filter((a) => a.currencyId === currencyId);

export const getBankAccountsForCustomer = (customerId) =>
  MOCK_BANK_ACCOUNTS.filter((a) => a.customerId === customerId);

export const getModalPayinQuote = ({
  amountCurrencyId,
  destinationCurrencyId,
  paymentMethodId,
  destinationRailId,
  amount,
}) => {
  const paymentMethod = getPaymentMethod(paymentMethodId);
  const originRailId = paymentMethod?.railId ?? "ach";

  const baseQuote = getPayinQuote({
    originCurrencyId: amountCurrencyId,
    destinationCurrencyId,
    originRailId,
    destinationRailId,
    amount,
  });

  const originRail = PAYMENT_RAILS[originRailId];
  const destinationRail = PAYMENT_RAILS[destinationRailId];

  return {
    ...baseQuote,
    paymentMethod,
    originRail,
    destinationRail,
    partnerFee: 0,
  };
};

export const getModalPayoutQuote = ({
  originCurrencyId,
  destinationCurrencyId,
  sendRailId,
  beneficiaryRailId,
  amount,
}) => {
  const baseQuote = getPayoutQuote({
    originCurrencyId,
    destinationCurrencyId,
    sendRailId,
    beneficiaryRailId,
    amount,
  });

  const sendRail = PAYMENT_RAILS[sendRailId];
  const beneficiaryRail = PAYMENT_RAILS[beneficiaryRailId];

  return {
    ...baseQuote,
    sendRail,
    beneficiaryRail,
    partnerFee: 0,
  };
};

export const formatTokenAmount = (value, tokenId) => {
  const decimals = tokenId === "usdt" || tokenId === "usdc" ? 2 : 2;
  return formatMonetaryValue(value, tokenId, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const MOCK_PAYOUT_VOLUME = {
  currencyId: "usd",
  todayTotal: 28450,
  yesterdayTotal: 25312,
  changePercent: 12.4,
  hourlyLabels: ["00h", "04h", "08h", "12h", "16h", "20h", "Agora"],
  hourlyValues: [1200, 2100, 4800, 9200, 15400, 22100, 28450],
  yesterdayHourlyValues: [900, 1800, 3600, 7100, 11800, 17600, 25312],
};
